import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  DEFAULT_GRAPH_LAYOUT,
  type DeviceGroupAttribute,
  type GraphPath,
  type Layout,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphLayout } from 'components/Graph/GraphLayout/GraphLayout';
import type { GraphLayoutChangeEvent } from 'components/Graph/GraphLayout/GraphLayoutItem';
import { SlidePaneClose } from 'components/SlidePane/SlidePaneClose';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo, useEffect, useState } from 'react';

export interface SaveLayoutButtonClickEvent {
  groupBy: DeviceGroupAttribute;
  layout: Layout;
  path: GraphPath;
}

interface Props {
  groupBy: DeviceGroupAttribute;
  onBackButtonClick: () => void;
  onSaveLayoutButtonClick: (event: SaveLayoutButtonClickEvent) => void;
  path: GraphPath;
  saving: boolean;
}

const BareGraphNetworkViewerDataInputLayout: FC<Props> = (props) => {
  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    pathLayout: prop('pathLayouts', props.groupBy, props.path),
  });
  const currentLayout: Layout = ctxProps.pathLayout ?? DEFAULT_GRAPH_LAYOUT;
  const [activeLayout, setActiveLayout] = useState<Layout>(currentLayout);

  const submitDisabled = currentLayout === activeLayout;

  const handleLayoutChange = useStableCallback(
    (event: GraphLayoutChangeEvent) => {
      setActiveLayout(event.layout);
    },
  );

  const handleSaveButtonClick = () => {
    props.onSaveLayoutButtonClick({
      groupBy: props.groupBy,
      layout: activeLayout,
      path: props.path,
    });
  };

  useEffect(() => {
    // Update layout when graph layout is open in sidebar and layout is changed in graph layout dialog
    setActiveLayout(currentLayout);
  }, [currentLayout]);

  return (
    <div className='flex flex-1 flex-col'>
      <SlidePaneClose
        className='graph-layout-settings-protocol-pane__close'
        onClose={props.onBackButtonClick}
        label={msg.canvasGraph.graphSidePanel.networkViewerLayout.back}
      />
      <div className='flex flex-1 flex-col bg-gray-50 p-5'>
        <div className='flex-1'>
          <GraphLayout
            activeLayout={activeLayout}
            groupBy={props.groupBy}
            layoutItemClassName='border-b border-t-0 border-l-0 border-r-0 border-solid border-gray-600'
            onChange={handleLayoutChange}
            path={props.path}
          />
        </div>
        <div className='flex justify-between'>
          <Button
            disabled={props.saving}
            onClick={props.onBackButtonClick}
            type='button'
            variant='secondary'
          >
            {msg.canvasGraph.graphSidePanel.networkViewerLayout.btnCancel}
          </Button>
          <Button
            disabled={submitDisabled}
            loading={props.saving}
            onClick={handleSaveButtonClick}
            size='sm'
            type='button'
            variant='primary'
          >
            {msg.canvasGraph.graphSidePanel.networkViewerLayout.btnSave}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const GraphNetworkViewerDataInputLayout = memo(
  BareGraphNetworkViewerDataInputLayout,
);
